import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Checkbox } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { handleSave } from "../utils/handleSave";
import { createTempId } from "../utils/createTempId";
import { FIXED_SELECT_FIELD_SX, TABLE_SCROLL_CONTAINER_SX, hasInvalidDateValue, hasValue, isRowComplete } from "../utils/formHelpers";
import { KOT_DATE_ERROR_MESSAGE, KOT_DATE_HELPER_TEXT, KOT_DISPLAY_DATE_FORMAT, parseKotStoredDate, toKotStorageDate } from "../utils/dateHelpers";
import { useKotFormDirty } from "../utils/useKotFormDirty";

const ReusableDatagrid = ({ title, columnsConfig, initialRows, section, mode }) => {
  const isView = mode === "view";
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);

  const emptyRowTemplate = useMemo(
    () =>
      columnsConfig.reduce((acc, col) => {
        acc[col.field] = "";
        return acc;
      }, {}),
    [columnsConfig],
  );

  const makeEmptyRow = useCallback(() => ({ id: createTempId(), ...emptyRowTemplate }), [emptyRowTemplate]);
  // With nothing saved, edit mode still seeds one blank row so there is somewhere to type.
  // View mode must not: a read-only section with no data would otherwise render a row of
  // empty disabled inputs rather than saying it is empty.
  const buildRows = useCallback(
    (nextRows = []) => {
      if (Array.isArray(nextRows) && nextRows.length > 0) {
        return nextRows.map((row) => ({ ...row }));
      }

      return isView ? [] : [makeEmptyRow()];
    },
    [isView, makeEmptyRow],
  );

  const [rows, setRows] = useState(buildRows(initialRows));
  const { isDirty, markDirty, markSaved, markReset, syncClean } = useKotFormDirty();

  // ✅ Selection state
  const [selectedRows, setSelectedRows] = useState([]);
  const [dateErrors, setDateErrors] = useState({});

  const clientId = useSelector((state) => state.kotClient?.clientId);
  const originalData = useSelector((state) => state.kotClient?.[section] || []);

  useEffect(() => {
    // Sync to Redux (initial fetch + post-save server IDs) to avoid temp-id drift
    setRows(buildRows(initialRows));
    setSelectedRows([]);
    setDateErrors({});
    syncClean();
  }, [buildRows, clientId, initialRows, syncClean]);

  const requiredFields = useMemo(() => columnsConfig.filter((col) => col.required !== false).map((col) => col.field), [columnsConfig]);
  const checkboxColumnWidth = 56;
  const getColumnWidth = useCallback((col) => col.width || col.minWidth || (col.type === "date" ? 220 : 240), []);
  const tableMinWidth = useMemo(() => (isView ? 0 : checkboxColumnWidth) + columnsConfig.reduce((sum, col) => sum + getColumnWidth(col), 0), [columnsConfig, getColumnWidth, isView]);

  // A row counts as "started" once any field has a value. Empty rows are ignored on Save.
  const rowHasAnyValue = useCallback((row) => columnsConfig.some((col) => hasValue(row[col.field])), [columnsConfig]);

  // Block adding while any row is missing mandatory fields (optional fields may stay empty).
  const hasIncompleteRow = useMemo(() => rows.some((row) => !isRowComplete(row, requiredFields, columnsConfig)), [requiredFields, rows, columnsConfig]);

  const hasDateValidationErrors = useMemo(() => {
    if (Object.values(dateErrors).some(Boolean)) {
      return true;
    }

    return rows.some((row) => columnsConfig.some((col) => col.type === "date" && hasInvalidDateValue(row[col.field])));
  }, [columnsConfig, dateErrors, rows]);

  const handleSelectRow = (id) => {
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
  };

  const handleSelectAll = (checked) => {
    setSelectedRows(checked ? rows.map((r) => r.id) : []);
  };

  const deleteSelectedRows = () => {
    markDirty();
    setRows((prev) => prev.filter((row) => !selectedRows.includes(row.id)));
    setSelectedRows([]);
  };

  // 🔹 Handle change
  const handleChange = (id, field, value) => {
    markDirty();
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;

        const updatedRow = { ...row, [field]: value };

        // Apply auto-population rules
        const columnConfig = columnsConfig.find((col) => col.field === field);
        if (columnConfig?.autoPopulate) {
          const { when, fields } = columnConfig.autoPopulate;
          if (when(value)) {
            // Auto-populate specified fields
            Object.assign(updatedRow, fields);
          }
        }

        return updatedRow;
      }),
    );
  };

  // 🔹 Add row
  // Allow a new row only once existing rows have their mandatory fields filled.
  // Optional fields may be left empty.
  const addRow = () => {
    if (hasIncompleteRow) {
      toast.warn("Complete the mandatory fields in the current row before adding another.");
      return;
    }

    markDirty();
    setRows((prev) => [...prev, makeEmptyRow()]);
  };

  // 🔹 Render cell dynamically
  const renderCell = (row, col) => {
    if (col.type === "select") {
      return (
        <TextField disabled={isView} select size="small" fullWidth value={row[col.field]} onChange={(e) => handleChange(row.id, col.field, e.target.value)} label={col.label} sx={FIXED_SELECT_FIELD_SX}>
          <MenuItem value="">Select</MenuItem>
          {col.options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (col.type === "date") {
      const rawValue = row[col.field];
      const parsedValue = parseKotStoredDate(rawValue);
      const errorKey = `${row.id}-${col.field}`;
      const hasStoredInvalidValue = hasInvalidDateValue(rawValue);
      const showDateError = Boolean(dateErrors[errorKey]) || hasStoredInvalidValue;

      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            disabled={isView}
            value={parsedValue && parsedValue.isValid() ? parsedValue : null}
            onChange={(newValue) => {
              if (!newValue) {
                handleChange(row.id, col.field, "");
                return;
              }

              if (newValue.isValid()) {
                handleChange(row.id, col.field, toKotStorageDate(newValue));
              }
            }}
            onError={(reason) => {
              setDateErrors((prev) => {
                const next = { ...prev };
                if (reason) {
                  next[errorKey] = true;
                } else {
                  delete next[errorKey];
                }
                return next;
              });
            }}
            inputFormat={KOT_DISPLAY_DATE_FORMAT}
            renderInput={(params) => (
              <TextField
                disabled={isView}
                {...params}
                error={showDateError || Boolean(params.error)}
                helperText={showDateError ? KOT_DATE_HELPER_TEXT : params.helperText}
                size="small"
                fullWidth
                placeholder={col.placeholder}
                onKeyDown={(e) => {
                  if (e.key !== "Backspace" && e.key !== "Delete" && e.key !== "Tab" && e.key !== "Enter") {
                    e.preventDefault();
                  }
                }}
              />
            )}
          />
        </LocalizationProvider>
      );
    }

    return <TextField disabled={isView} size="small" fullWidth multiline minRows={1} placeholder={col.placeholder} value={row[col.field]} onChange={(e) => handleChange(row.id, col.field, e.target.value)} />;
  };

  const saveData = async () => {
    if (hasDateValidationErrors) {
      toast.error(KOT_DATE_ERROR_MESSAGE);
      return;
    }

    // Validate mandatory fields on submit for any started row (empty rows are ignored).
    const hasIncompleteStartedRow = rows.some((row) => rowHasAnyValue(row) && !isRowComplete(row, requiredFields, columnsConfig));
    if (hasIncompleteStartedRow) {
      toast.error("Please complete all mandatory fields before saving.");
      return;
    }

    setIsSaving(true);
    try {
      const result = await handleSave(dispatch, section, rows, clientId, originalData);
      if (result?.status !== "error") {
        markSaved();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    markReset();
    setRows(buildRows(initialRows));
    setSelectedRows([]);
    setDateErrors({});
  };

  return (
    <Box p={4} bgcolor="#fff" borderRadius={2}>
      {/* Header */}
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h3">{title}</Typography>

        {!isView && selectedRows.length > 0 && (
          <Button variant="contained" color="error" onClick={deleteSelectedRows}>
            Delete ({selectedRows.length})
          </Button>
        )}
      </Box>

      {/* Table */}
      <Box sx={TABLE_SCROLL_CONTAINER_SX}>
        <Table sx={{ minWidth: Math.max(tableMinWidth, 720), tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              {/* Select All */}
              {!isView && (
                <TableCell padding="checkbox" sx={{ width: checkboxColumnWidth }}>
                  <Checkbox data-ignore-dirty="true" disabled={isView} checked={rows.length > 0 && selectedRows.length === rows.length} indeterminate={selectedRows.length > 0 && selectedRows.length < rows.length} onChange={(e) => handleSelectAll(e.target.checked)} />
                </TableCell>
              )}

              {columnsConfig.map((col) => (
                <TableCell key={col.field} sx={{ width: getColumnWidth(col), minWidth: getColumnWidth(col) }}>
                  {col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Reachable in view mode whenever the section has no data, and in edit mode
                once every row has been deleted. */}
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnsConfig.length + (isView ? 0 : 1)} align="center" sx={{ py: 5, color: "text.secondary", borderBottom: "none" }}>
                  <Typography>No records to show</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} selected={selectedRows.includes(row.id)}>
                  {/* Checkbox */}
                  {!isView && (
                    <TableCell padding="checkbox" sx={{ width: checkboxColumnWidth }}>
                      <Checkbox data-ignore-dirty="true" disabled={isView} checked={selectedRows.includes(row.id)} onChange={() => handleSelectRow(row.id)} />
                    </TableCell>
                  )}

                  {columnsConfig.map((col) => (
                    <TableCell key={col.field} sx={{ verticalAlign: "top", width: getColumnWidth(col), minWidth: getColumnWidth(col) }}>
                      {renderCell(row, col)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Add Row */}
      {!isView && (
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button variant="outlined" onClick={addRow}>
            Add More
          </Button>
        </Box>
      )}

      {/* Actions — disabled until the form has unsaved changes */}
      {!isView && (
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={handleCancel} disabled={isSaving || !isDirty}>Cancel</Button>
          <Button variant="contained" onClick={saveData} disabled={isSaving || !isDirty}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ReusableDatagrid;
